import { FC, MouseEvent, useEffect, useMemo, useState } from "react";
import { handleCourseUrl } from "@utils/handleCourseUrl";
import Link from "next/link";
import ProductBody from "./product-body";
import { ICartInput } from "types/api/common";
import BookmarkBold from "@svg/new-design/bookmark-bold";
import { modalActions } from "@context/modals";
import { MODAL_TYPES } from "types/modals";
import Bookmark from "@svg/new-design/bookmark";
import { useIsMobile } from "@shared/hooks/useIsMobile";
import { useCardAction } from "@shared/components/card/useCartAction";
import { FAVORITE_TYPE } from "enums/favorite-type";
import {
  ICON_COLOR,
  ICON_SIZE,
  ICON_VARIANT,
  IconButton,
} from "@shared/components/icon-button";
import ImageComponent from "@shared/components/image";
import { resolveProductImageSources } from "utils/product-image";

const HowToLearnProductCard: FC<ICartInput> = ({
  svg_featured_image,
  png_featured_image,
  svg_image,
  png_image,
  title,
  total_orders,
  sku,
  duration,
  type,
  slug,
  hasCertificate,
  instructors,
  student_count,
  is_favorite,
  id,
}) => {
  const href = handleCourseUrl(type, slug || sku);
  const isMobile = useIsMobile(1024, true);
  const { addFavoriteCourse } = useCardAction();
  const [favorite, setFavorite] = useState(is_favorite);
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    setFavorite(is_favorite);
  }, [is_favorite]);

  const imageSources = useMemo(
    () =>
      resolveProductImageSources({
        svg_image,
        png_image,
        svg_featured_image,
        png_featured_image,
      }),
    [svg_image, png_image, svg_featured_image, png_featured_image]
  );

  const handleFavorite = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (favorite) {
      modalActions.addModal(MODAL_TYPES.REMOVE_CONFIRMATION, {
        data: {
          title: "حذف آموزش از علاقه‌مندی‌های من",
          description: `${title} از فهرست علاقه‌مندی‌های من حذف شود؟`,
          onSubmit: submitFavorite,
        },
        config: {
          hideCloseIcon: true,
          modalClassName: "rounded-t-24 rounded-b-16",
        },
      });
    } else {
      submitFavorite();
    }
  };

  const submitFavorite = async () => {
    modalActions.clearModals();
    setIsLoading(true);
    const res = await addFavoriteCourse?.(favorite, id, FAVORITE_TYPE.PRODUCT);

    setIsLoading(false);
    setFavorite(res);
  };

  return (
    <Link href={href} prefetch={false}>
      <div className="md:p-4 md:pl-2 py-2 px-2.5 relative hover:shadow-brand-md  items-start  w-full  active:bg-fds-canvas-secondary  rounded-8 hover:border-fds-brand-light active:border-fds-brand border-fds-default border">
        <div className=" flex gap-2  md:gap-x-4    ">
          <div className="rounded-6 grow shrink-0 relative collectionPicture overflow-hidden group">
            <div className="z-10 rounded-2 absolute right-1 top-1 w-6 h-6 lg:top-1.5 lg:right-1.5 lg:w-7 lg:h-7 pointer-events-none bg-white/[0.72] group-has-[button:hover]:bg-white/[0.88]" />
            <div className="absolute top-0 right-0 z-10 text-fds-secondary">
              <IconButton
                variant={ICON_VARIANT.DEFAULT}
                color={ICON_COLOR.DEFAULT}
                onClick={handleFavorite}
                icon={favorite ? <BookmarkBold /> : <Bookmark />}
                size={isMobile ? ICON_SIZE.SMALL : ICON_SIZE.MEDIUM}
                disabled={isLoading}
                aria-label={
                  favorite ? "حذف از علاقمندی‌ها" : "افزودن به علاقمندی"
                }
                message={favorite ? "حذف از علاقمندی‌ها" : "افزودن به علاقمندی"}
                iconClassName={favorite ? "!text-fds-icon-brand" : ""}
                className="hover:!bg-transparent active:!bg-transparent"
              />
            </div>
            <ImageComponent
              fill
              src={imageSources.src}
              fallbackSrcs={imageSources.fallbackSrcs}
              alt={title}
              className="object-cover"
            />
          </div>
          <div className="w-full  ">
            <div className="flex items-start justify-between gap-2 pl-2">
              <p className=" min-h-10 md:min-h-full lg:min-h-fds-52 md:line-clamp-1 lg:line-clamp-2 line-clamp-2 md:title-lg title-sm ">
                {title}
              </p>
            </div>

            <ProductBody
              duration={duration}
              instructor={instructors}
              hasCertificate={hasCertificate || false}
              total_orders={total_orders}
              student_count={student_count}
            />
          </div>
        </div>
      </div>
    </Link>
  );
};
export default HowToLearnProductCard;
